import { useId, useRef, useState, type KeyboardEvent } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import {
  contentionMessages,
  type VariableResponse,
} from "../../lib/variableCascade";
import { VariableValueField } from "../variables/VariableValueField";
import {
  parseVariableValue,
  variableFormError,
  type VariableFormState,
} from "../variables/variableFormState";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  Popover,
  RouterLink,
  Select,
  Stack,
  Text,
  TextInput,
} from "../../ui";

type ScenarioReference = components["schemas"]["ScenarioReferenceResponse"];
type ReservedVariableKey = components["schemas"]["ReservedVariableKeyResponse"];
type VariableValueType = components["schemas"]["VariableValueType"];
type BindOutcome = "persona" | "constant" | "scenario";

export interface ScenarioTokenRowProps {
  agentId: string;
  group: VariableResponse[];
  keyRulesError: boolean;
  keyRulesPending: boolean;
  reference: ScenarioReference | undefined;
  reservedKeys: ReservedVariableKey[] | undefined;
  scenarioId: string | undefined;
  token: string;
}

export function ScenarioTokenRow({
  agentId,
  group,
  keyRulesError,
  keyRulesPending,
  reference,
  reservedKeys,
  scenarioId,
  token,
}: ScenarioTokenRowProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [outcome, setOutcome] = useState<BindOutcome>("persona");
  const [valueType, setValueType] = useState<VariableValueType>("string");
  const [allowedValues, setAllowedValues] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdOutcome, setCreatedOutcome] = useState<BindOutcome | null>(
    null,
  );
  const isBroken = reference?.key === "unavailable_variable";
  const reserved = reservedKeys?.find(
    (rule) => unicodeCaseFold(rule.key) === unicodeCaseFold(token),
  );
  const rulesUnavailable =
    keyRulesPending || keyRulesError || reservedKeys === undefined;
  const isUnbound = !isBroken && reference?.variable_id == null;
  const isLocallyDeclared = createdOutcome !== null;
  const scenarioSlotPersonasCanFill = group.find(
    (variable) =>
      variable.scope_type === "scenario" &&
      variable.scope_id === scenarioId &&
      variable.current_revision.is_required &&
      variable.current_revision.default_value === null,
  );
  const parsedAllowedValues = allowedValues
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  // One validator, one message set: the popover reuses the Variables editor's
  // rules instead of re-deriving them. Reserved keys and unavailable key rules
  // are reported by this popover's own InlineError, so no rules are passed.
  const bindFormState: VariableFormState = {
    allowedValues,
    defaultValue,
    isRequired: outcome === "persona",
    isSensitive: false,
    key: token,
    scopeId: outcome === "constant" ? agentId : (scenarioId ?? ""),
    scopeType: outcome === "constant" ? "agent" : "scenario",
    valueType,
  };
  const declarationError =
    scenarioId === undefined
      ? null
      : variableFormError(bindFormState, false, false, []);
  const allowedValuesError =
    valueType === "enum" && parsedAllowedValues.length === 0
      ? declarationError
      : null;
  const defaultError =
    outcome === "persona"
      ? null
      : defaultValue.trim().length === 0
        ? "Default value is required."
        : allowedValuesError === null
          ? declarationError
          : null;
  const existingScopeNotice = group[0]
    ? `“${group[0].key}” is already declared on 1 scope ` +
      `(${group[0].scope_type}). Adding this declaration creates contention.`
    : "";
  const sensitiveNotice = group[0]?.current_revision.is_sensitive
    ? " One declaration is sensitive, so this value is masked everywhere."
    : "";
  const existingDeclarationNotice =
    group.length > 1
      ? contentionMessages(group).join(" ")
      : existingScopeNotice + sensitiveNotice;
  const creationDisabled =
    isCreating ||
    isLocallyDeclared ||
    rulesUnavailable ||
    reserved !== undefined ||
    defaultError !== null ||
    allowedValuesError !== null;

  async function createDeclaration() {
    if (!scenarioId || creationDisabled) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const common = {
        // ASSUMED(bind enum declares value_type and allowed_values)
        allowed_values: valueType === "enum" ? parsedAllowedValues : null,
        // ASSUMED(bind declarations are non-sensitive)
        is_sensitive: false,
        key: token,
        value_type: valueType,
      } satisfies Partial<components["schemas"]["VariableCreate"]>;
      let body: components["schemas"]["VariableCreate"];
      if (outcome === "constant") {
        // ASSUMED(bind outcomes declare is_required false — #2203 R2 leaves is_required unruled)
        body = {
          ...common,
          default_value: parseVariableValue(
            token,
            valueType,
            parsedAllowedValues,
            defaultValue,
          ),
          is_required: false,
          scope_id: agentId,
          scope_type: "agent",
        };
      } else if (outcome === "scenario") {
        // ASSUMED(bind outcomes declare is_required false — #2203 R2 leaves is_required unruled)
        body = {
          ...common,
          default_value: parseVariableValue(
            token,
            valueType,
            parsedAllowedValues,
            defaultValue,
          ),
          is_required: false,
          scope_id: scenarioId,
          scope_type: "scenario",
        };
      } else {
        body = {
          ...common,
          default_value: null,
          is_required: true,
          scope_id: scenarioId,
          scope_type: "scenario",
        };
      }
      const { data, error } = await apiClient.POST("/v1/variables", { body });
      if (error) throw new ApiRequestError(error);
      queryClient.setQueryData(queryKeys.variables.detail(data.id), data);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.variables.all,
      });
      setCreatedOutcome(outcome);
      setIsOpen(false);
    } catch (caught) {
      setCreateError(requestErrorDescription(caught));
    } finally {
      setIsCreating(false);
    }
  }

  function submitTextInput(event: KeyboardEvent<HTMLElement>) {
    if (event.defaultPrevented || event.key !== "Enter") return;
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== "text") return;
    event.preventDefault();
    event.stopPropagation();
    if (!creationDisabled) void createDeclaration();
  }

  // ASSUMED(saved unbound declaration gets neutral Declared badge)
  const declaredStatus = "Declared";
  const brokenReferenceCaption =
    "Reinsert this variable; its saved reference is unavailable.";
  const locallyDeclaredCaption = "Save the Scenario to use this variable.";
  const baseStatus = isBroken
    ? "Broken reference"
    : isLocallyDeclared
      ? declaredStatus
      : reference?.variable_id != null
        ? "Bound by id"
        : group.length === 0 || reference !== undefined
          ? "Nothing declares this"
          : declaredStatus;

  return (
    <Stack gap={2}>
      <Inline align="center" gap={2}>
        <Text>{token}</Text>
        <Badge
          tone={
            baseStatus === "Broken reference" ||
            baseStatus === "Nothing declares this"
              ? "danger"
              : "neutral"
          }
        >
          {baseStatus}
        </Badge>
        {scenarioSlotPersonasCanFill ? <Badge>Persona-supplied</Badge> : null}
        {group.length > 1 ? <Badge>Contended</Badge> : null}
      </Inline>
      {isBroken ? (
        <InlineError>{brokenReferenceCaption}</InlineError>
      ) : isLocallyDeclared ? (
        <Text variant="muted">{locallyDeclaredCaption}</Text>
      ) : baseStatus === "Nothing declares this" ? (
        <InlineError>Declare this variable before launching.</InlineError>
      ) : null}
      {group.length > 1 ? (
        <Text variant="muted">{contentionMessages(group).join(" ")}</Text>
      ) : null}
      {isUnbound ? (
        <Inline gap={2}>
          <div className="marker-popover-anchor">
            <Button
              aria-controls={popoverId}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              disabled={scenarioId === undefined || isLocallyDeclared}
              ref={anchorRef}
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => {
                setIsOpen((current) => !current);
              }}
            >
              Bind
            </Button>
            <Popover
              aria-label={`Bind ${token} variable`}
              anchorRef={anchorRef}
              id={popoverId}
              isOpen={isOpen}
              onDismiss={() => {
                setIsOpen(false);
              }}
              onKeyDown={submitTextInput}
              placement="inline"
              size="wide"
            >
              <Stack gap={3}>
                {group.length > 0 ? (
                  <Stack gap={1}>
                    <Badge>
                      {group.length > 1 ? "Contended" : "Existing declaration"}
                    </Badge>
                    <Text>{existingDeclarationNotice}</Text>
                    {group.length === 1 ? (
                      <RouterLink
                        target="_blank"
                        rel="noopener noreferrer"
                        params={{ agentId }}
                        search={{ variable: group[0]?.id }}
                        to="/agents/$agentId/variables"
                      >
                        Edit the existing declaration
                      </RouterLink>
                    ) : (
                      group.map((variable) => (
                        <RouterLink
                          target="_blank"
                          rel="noopener noreferrer"
                          key={variable.id}
                          params={{ agentId }}
                          search={{ variable: variable.id }}
                          to="/agents/$agentId/variables"
                        >
                          Edit the existing {variable.scope_type} declaration
                        </RouterLink>
                      ))
                    )}
                  </Stack>
                ) : null}
                <fieldset>
                  <legend>How is this value supplied?</legend>
                  <Stack gap={2}>
                    {(
                      [
                        ["persona", "A persona supplies it"],
                        ["scenario", "This scenario owns it"],
                        ["constant", "An agent-wide constant"],
                      ] as const
                    ).map(([value, label]) => (
                      <label key={value}>
                        <input
                          checked={outcome === value}
                          name={`bind-${token}`}
                          type="radio"
                          value={value}
                          onChange={() => {
                            setOutcome(value);
                          }}
                        />{" "}
                        {label}
                      </label>
                    ))}
                  </Stack>
                </fieldset>
                <Select
                  label="Value type"
                  value={valueType}
                  onChange={(event) => {
                    setValueType(
                      event.currentTarget.value as VariableValueType,
                    );
                    setAllowedValues("");
                    setDefaultValue("");
                  }}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="bool">Boolean</option>
                  <option value="enum">Enum</option>
                </Select>
                {valueType === "enum" ? (
                  <TextInput
                    description="Comma-separated values."
                    error={allowedValuesError}
                    label="Allowed values"
                    onKeyDown={submitTextInput}
                    required
                    value={allowedValues}
                    onChange={(event) => {
                      setAllowedValues(event.currentTarget.value);
                    }}
                  />
                ) : null}
                {outcome !== "persona" ? (
                  <Stack gap={1}>
                    <VariableValueField
                      allowedValues={parsedAllowedValues}
                      emptyLabel={
                        valueType === "bool" || valueType === "enum"
                          ? "Select a value"
                          : undefined
                      }
                      error={defaultError}
                      label="Default value"
                      required
                      value={defaultValue}
                      valueType={valueType}
                      onChange={setDefaultValue}
                      onKeyDown={submitTextInput}
                    />
                    {outcome === "scenario" ? (
                      <Text variant="muted">
                        This default overrides every persona value for this key.
                      </Text>
                    ) : null}
                  </Stack>
                ) : null}
                {/* Planner _variables applies scenario defaults after persona
                    values, so the persona outcome intentionally exposes no
                    default field or default-based escape hatch. */}
                {rulesUnavailable ? (
                  <InlineError>
                    Variable key rules are unavailable. Creation is disabled.
                  </InlineError>
                ) : reserved ? (
                  <InlineError>
                    {token} is reserved as a {reserved.kind}.
                  </InlineError>
                ) : null}
                {createError ? <InlineError>{createError}</InlineError> : null}
                <Inline>
                  <Button
                    disabled={creationDisabled}
                    type="button"
                    onClick={() => void createDeclaration()}
                  >
                    {isCreating ? "Creating…" : "Create declaration"}
                  </Button>
                </Inline>
              </Stack>
            </Popover>
          </div>
          {scenarioId === undefined ? (
            <Text variant="muted">Save the scenario first.</Text>
          ) : isLocallyDeclared ? (
            <Text variant="muted">Save the scenario before binding again.</Text>
          ) : null}
          <RouterLink
            target="_blank"
            rel="noopener noreferrer"
            params={{ agentId }}
            search={{
              key: token,
              new: 1,
              scope_id: scenarioId,
              scope_type: "scenario",
            }}
            to="/agents/$agentId/variables"
          >
            Open in Variables
          </RouterLink>
        </Inline>
      ) : null}
    </Stack>
  );
}
